import encrypt from "./encrypt"
import { log } from "../utils/logger";

const REGISTER_API_BASE = "https://register.vectorvault.io";

/**
 * Get current onboarding status for a user
 * @param {string} userEmail - User's email
 * @returns {Promise<Object>} Onboarding status object
 */
export const getOnboardingStatus = async (userEmail) => {
    try {
        const encryptedEmail = encrypt(userEmail.toLowerCase());
        
        const formData = new URLSearchParams();
        formData.append('email', encryptedEmail);
        
        const response = await fetch(`${REGISTER_API_BASE}/onboarding-status`, {
            method: 'POST',
            headers: {
                "Authorization": 'unexpected_authorization_code'
            },
            body: formData
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.error(`HTTP Error: ${response.status}`, errorText);
            throw new Error(`Failed to get onboarding status: ${errorText}`);
        }

        const result = await response.json();
        log('Onboarding status retrieved:', result);
        return result.onboarding;
    } catch (error) {
        console.error("Error getting onboarding status:", error);
        throw error;
    }
};

/**
 * Mark an onboarding step as completed
 * @param {string} userEmail - User's email
 * @param {number} stepNumber - Step number (1-6)
 * @returns {Promise<Object>} Updated onboarding status
 */
export const completeOnboardingStep = async (userEmail, stepNumber) => {
    try {
        const encryptedEmail = encrypt(userEmail.toLowerCase());
        
        const formData = new URLSearchParams();
        formData.append('email', encryptedEmail);
        formData.append('step_number', stepNumber.toString());
        
        const response = await fetch(`${REGISTER_API_BASE}/onboarding-step`, {
            method: 'POST',
            headers: {
                "Authorization": 'unexpected_authorization_code'
            },
            body: formData
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.error(`HTTP Error: ${response.status}`, errorText);
            throw new Error(`Failed to complete onboarding step: ${errorText}`);
        }

        const result = await response.json();
        console.log(`Onboarding step ${stepNumber} API response:`, result);
        log(`Onboarding step ${stepNumber} completed:`, result);
        return result.onboarding;
    } catch (error) {
        console.error("Error completing onboarding step:", error);
        throw error;
    }
};

/**
 * Skip the entire onboarding process
 * @param {string} userEmail - User's email
 * @returns {Promise<Object>} Updated onboarding status
 */
export const skipOnboarding = async (userEmail) => {
    try {
        const encryptedEmail = encrypt(userEmail.toLowerCase());
        
        const formData = new URLSearchParams();
        formData.append('email', encryptedEmail);
        
        const response = await fetch(`${REGISTER_API_BASE}/onboarding-skip`, {
            method: 'POST',
            headers: {
                "Authorization": 'unexpected_authorization_code'
            },
            body: formData
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.error(`HTTP Error: ${response.status}`, errorText);
            throw new Error(`Failed to skip onboarding: ${errorText}`);
        }

        const result = await response.json();
        console.log('Onboarding skip API response:', result);
        log('Onboarding skipped:', result);
        return result.onboarding;
    } catch (error) {
        console.error("Error skipping onboarding:", error);
        throw error;
    }
};

/**
 * Reset onboarding process (for debugging/admin)
 * @param {string} userEmail - User's email
 * @returns {Promise<Object>} Reset onboarding status
 */
export const resetOnboarding = async (userEmail) => {
    try {
        const encryptedEmail = encrypt(userEmail.toLowerCase());
        
        const formData = new URLSearchParams();
        formData.append('email', encryptedEmail);
        
        const response = await fetch(`${REGISTER_API_BASE}/onboarding-reset`, {
            method: 'POST',
            headers: {
                "Authorization": 'unexpected_authorization_code'
            },
            body: formData
        });

        if (!response.ok) {
            const errorText = await response.text();
            console.error(`HTTP Error: ${response.status}`, errorText);
            throw new Error(`Failed to reset onboarding: ${errorText}`);
        }

        const result = await response.json();
        console.log('Onboarding reset API response:', result);
        log('Onboarding reset:', result);
        return result.onboarding;
    } catch (error) {
        console.error("Error resetting onboarding:", error);
        throw error;
    }
};

// Step definitions mapping
export const ONBOARDING_STEPS = {
    CREATE_VAULT: 1,
    ADD_OPENAI_KEY: 2,
    ADD_DATA_TO_VAULT: 3,
    TALK_TO_VAULT: 4,
    MAKE_DEPLOYMENT: 5,
    MAKE_FLOW: 6
};

// Helper function to check if a step is completed
export const isStepCompleted = (onboardingStatus, stepNumber) => {
    return onboardingStatus?.completed_steps?.includes(stepNumber) || false;
};

// Helper function to check if onboarding is complete
export const isOnboardingComplete = (onboardingStatus) => {
    // Onboarding is complete if:
    // 1. It was explicitly skipped, OR
    // 2. It was explicitly completed (has a completed_at timestamp)
    return onboardingStatus?.skipped === true || (onboardingStatus?.completed_at && onboardingStatus.completed_at !== null);
}; 